import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Home, Package, Truck, Users, Calendar, FileText, Clock,
  ChevronRight, Search, Filter, Eye, Edit, Trash2, Send,
  Download, Phone, Mail, MapPin, MessageCircle, Plus,
  CheckCircle, XCircle, AlertCircle, User, Euro, Calculator,
  Box, Building, ChevronDown, ChevronUp, RefreshCw
} from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';

const STATUS_CONFIG = {
  nouveau: { label: 'Nouvelle demande', color: 'bg-blue-100 text-blue-800', icon: AlertCircle },
  en_cours: { label: 'En cours de traitement', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  devis_envoye: { label: 'Devis envoyé', color: 'bg-purple-100 text-purple-800', icon: FileText },
  accepte: { label: 'Accepté', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  refuse: { label: 'Refusé', color: 'bg-red-100 text-red-800', icon: XCircle },
  planifie: { label: 'Mission planifiée', color: 'bg-indigo-100 text-indigo-800', icon: Calendar },
  termine: { label: 'Terminé', color: 'bg-gray-100 text-gray-800', icon: CheckCircle }
};

// Configuration des calculs automatiques
const DEVIS_CONFIG = {
  prix_km: 3.25,
  prix_volume_m3: 30,
  prix_demenageur_jour: 120,
  prix_carton: 3.80,
  prix_stockage_24h: 90,
  prix_stockage_48h: 160,
  prix_protection: 120,
  prix_emballage_fragile: 80,
  minimum_devis: 650,
};

// Conversion surface -> volume
const SURFACE_TO_VOLUME = {
  20: 10, 30: 15, 40: 20, 50: 25, 60: 30, 70: 35, 80: 40, 90: 45, 100: 50, 120: 60, 150: 75
};

// Camions disponibles
const CAMIONS = [
  { value: '14', label: 'Camion 14 m³' },
  { value: '20', label: 'Camion 20 m³' },
  { value: '30', label: 'Camion 30 m³' },
  { value: '40', label: 'Camion 40 m³' },
  { value: '2x30', label: '2 x Camion 30 m³' },
];

// Fonctions de calcul automatique
const calculerCamion = (volume) => {
  if (volume <= 14) return '14';
  if (volume <= 20) return '20';
  if (volume <= 30) return '30';
  if (volume <= 40) return '40';
  return '2x30';
};

const calculerDemenageurs = (volume) => {
  if (volume <= 20) return 2;
  if (volume <= 40) return 3;
  if (volume <= 70) return 4;
  return 5;
};

const calculerDuree = (volume) => {
  if (volume <= 30) return 1;
  if (volume <= 50) return 1.5;
  if (volume <= 70) return 2;
  return 3;
};

const AdminDemenagement = ({ user, activeModule = "demenagement", view = "dashboard" }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(view === 'dashboard' ? 'demandes' : view);
  const [demandes, setDemandes] = useState([]);
  const [devisList, setDevisList] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Modal states
  const [selectedDemande, setSelectedDemande] = useState(null);
  const [showDemandeModal, setShowDemandeModal] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [showPlanningModal, setShowPlanningModal] = useState(false);
  const [savingQuote, setSavingQuote] = useState(false);
  
  // Formulaire de devis complet
  const [quoteForm, setQuoteForm] = useState({
    // Client
    client_nom: '',
    client_telephone: '',
    client_email: '',
    
    // Adresse départ
    depart_adresse: '',
    depart_code_postal: '',
    depart_ville: '',
    depart_etage: 0,
    depart_ascenseur: false,
    
    // Adresse arrivée
    arrivee_adresse: '',
    arrivee_code_postal: '',
    arrivee_ville: '',
    arrivee_etage: 0,
    arrivee_ascenseur: false,
    
    // Distance
    distance_km: '',
    distance_auto: false,
    
    // Volume
    mode_volume: 'volume', // 'volume' ou 'surface'
    volume_m3: '',
    surface_m2: '',
    
    // Ressources (calculées puis modifiables)
    camion: '',
    nb_demenageurs: '',
    nb_jours: '',
    
    // Options
    nb_cartons: 0,
    stockage: '', // '', '24h', '48h'
    protection_complete: false,
    emballage_fragile: false,
    
    // Prix (calculés puis modifiables)
    prix_transport: '',
    prix_volume: '',
    prix_main_oeuvre: '',
    prix_cartons: '',
    prix_stockage: '',
    prix_options: '',
    
    // Ajustement manuel
    ajustement: '',
    ajustement_description: '',
    
    // Notes
    notes: '',
    date_souhaitee: '',
  });

  // Planning form
  const [planningData, setPlanningData] = useState({
    date: '',
    time: '',
    team: [],
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [demandesRes, employeesRes, devisRes] = await Promise.all([
        axios.get(`${API}/demandes-client`, { withCredentials: true }),
        axios.get(`${API}/users?status=active`, { withCredentials: true }),
        axios.get(`${API}/devis/liste`, { withCredentials: true }).catch(() => ({ data: { devis: [] } }))
      ]);
      
      // Filter only demenagement requests
      const demenagementDemandes = (demandesRes.data || []).filter(d => d.request_type === 'demenagement');
      setDemandes(demenagementDemandes);
      setEmployees((employeesRes.data || []).filter(e => e.role === 'employee'));
      setDevisList(devisRes.data?.devis || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculer le volume à partir de la surface
  const volumeFromSurface = useMemo(() => {
    const surface = parseFloat(quoteForm.surface_m2) || 0;
    // Approximation: 1m² ≈ 0.5m³
    return Math.round(surface * 0.5);
  }, [quoteForm.surface_m2]);

  // Volume effectif
  const volumeEffectif = useMemo(() => {
    if (quoteForm.mode_volume === 'surface') {
      return volumeFromSurface;
    }
    return parseFloat(quoteForm.volume_m3) || 0;
  }, [quoteForm.mode_volume, quoteForm.volume_m3, volumeFromSurface]);

  // Calculs automatiques
  const calculsAuto = useMemo(() => {
    const volume = volumeEffectif;
    const distance = parseFloat(quoteForm.distance_km) || 0;
    const nbDemenageurs = quoteForm.nb_demenageurs ? parseInt(quoteForm.nb_demenageurs) : calculerDemenageurs(volume);
    const nbJours = quoteForm.nb_jours ? parseFloat(quoteForm.nb_jours) : calculerDuree(volume);
    
    const prixTransport = distance * DEVIS_CONFIG.prix_km;
    const prixVolume = volume * DEVIS_CONFIG.prix_volume_m3;
    const prixMainOeuvre = nbDemenageurs * DEVIS_CONFIG.prix_demenageur_jour * nbJours;
    const prixCartons = (quoteForm.nb_cartons || 0) * DEVIS_CONFIG.prix_carton;
    
    let prixStockage = 0;
    if (quoteForm.stockage === '24h') prixStockage = DEVIS_CONFIG.prix_stockage_24h;
    if (quoteForm.stockage === '48h') prixStockage = DEVIS_CONFIG.prix_stockage_48h;
    
    let prixOptions = 0;
    if (quoteForm.protection_complete) prixOptions += DEVIS_CONFIG.prix_protection;
    if (quoteForm.emballage_fragile) prixOptions += DEVIS_CONFIG.prix_emballage_fragile;
    
    const sousTotal = prixTransport + prixVolume + prixMainOeuvre + prixCartons + prixStockage + prixOptions;
    const ajustement = parseFloat(quoteForm.ajustement) || 0;
    const total = Math.max(DEVIS_CONFIG.minimum_devis, Math.round((sousTotal + ajustement) / 10) * 10);
    
    return {
      camion_suggere: calculerCamion(volume),
      demenageurs_suggeres: calculerDemenageurs(volume),
      jours_suggeres: calculerDuree(volume),
      prix_transport: Math.round(prixTransport * 100) / 100,
      prix_volume: Math.round(prixVolume * 100) / 100,
      prix_main_oeuvre: Math.round(prixMainOeuvre * 100) / 100,
      prix_cartons: Math.round(prixCartons * 100) / 100,
      prix_stockage: prixStockage,
      prix_options: prixOptions,
      sous_total: Math.round(sousTotal * 100) / 100,
      total: total
    };
  }, [volumeEffectif, quoteForm]);

  // Prix total final (avec overrides manuels)
  const prixFinal = useMemo(() => {
    const transport = quoteForm.prix_transport !== '' ? parseFloat(quoteForm.prix_transport) : calculsAuto.prix_transport;
    const volume = quoteForm.prix_volume !== '' ? parseFloat(quoteForm.prix_volume) : calculsAuto.prix_volume;
    const mainOeuvre = quoteForm.prix_main_oeuvre !== '' ? parseFloat(quoteForm.prix_main_oeuvre) : calculsAuto.prix_main_oeuvre;
    const cartons = quoteForm.prix_cartons !== '' ? parseFloat(quoteForm.prix_cartons) : calculsAuto.prix_cartons;
    const stockage = quoteForm.prix_stockage !== '' ? parseFloat(quoteForm.prix_stockage) : calculsAuto.prix_stockage;
    const options = quoteForm.prix_options !== '' ? parseFloat(quoteForm.prix_options) : calculsAuto.prix_options;
    const ajustement = parseFloat(quoteForm.ajustement) || 0;
    
    const sousTotal = transport + volume + mainOeuvre + cartons + stockage + options;
    return Math.max(DEVIS_CONFIG.minimum_devis, Math.round((sousTotal + ajustement) / 10) * 10);
  }, [quoteForm, calculsAuto]);

  // Pré-remplir le formulaire depuis une demande
  const prefillFromDemande = (demande) => {
    setQuoteForm(prev => ({
      ...prev,
      client_nom: demande.name || '',
      client_telephone: demande.phone || '',
      client_email: demande.email || '',
      depart_adresse: demande.address_from || '',
      arrivee_adresse: demande.address_to || '',
      volume_m3: demande.volume?.replace(/[^0-9.]/g, '') || '',
      date_souhaitee: demande.date || '',
      notes: demande.message || '',
    }));
  };

  const updateDemandeStatus = async (demandeId, newStatus) => {
    try {
      await axios.put(`${API}/demandes-client/${demandeId}/status?status=${newStatus}`, {}, { withCredentials: true });
      setDemandes(prev => prev.map(d => 
        d.demande_id === demandeId ? { ...d, status: newStatus } : d
      ));
      if (selectedDemande?.demande_id === demandeId) {
        setSelectedDemande(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      console.error('Failed to update status:', error);
      alert('Erreur lors de la mise à jour du statut');
    }
  };

  const deleteDemande = async (demandeId) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette demande ?')) return;
    try {
      await axios.delete(`${API}/demandes-client/${demandeId}`, { withCredentials: true });
      setDemandes(prev => prev.filter(d => d.demande_id !== demandeId));
      setShowDemandeModal(false);
    } catch (error) {
      console.error('Failed to delete:', error);
      alert('Erreur lors de la suppression');
    }
  };

  // Sauvegarder le devis
  const saveQuote = async (sendEmail = false) => {
    setSavingQuote(true);
    try {
      const volume = volumeEffectif;
      const camion = quoteForm.camion || calculsAuto.camion_suggere;
      const nbDemenageurs = quoteForm.nb_demenageurs || calculsAuto.demenageurs_suggeres;
      const nbJours = quoteForm.nb_jours || calculsAuto.jours_suggeres;
      
      const devisData = {
        client_nom: quoteForm.client_nom,
        client_email: quoteForm.client_email,
        client_telephone: quoteForm.client_telephone,
        
        adresse_depart: `${quoteForm.depart_adresse}, ${quoteForm.depart_code_postal} ${quoteForm.depart_ville}`.trim(),
        adresse_arrivee: `${quoteForm.arrivee_adresse}, ${quoteForm.arrivee_code_postal} ${quoteForm.arrivee_ville}`.trim(),
        
        conditions_acces: {
          depart: {
            etage: quoteForm.depart_etage,
            ascenseur: quoteForm.depart_ascenseur
          },
          arrivee: {
            etage: quoteForm.arrivee_etage,
            ascenseur: quoteForm.arrivee_ascenseur
          }
        },
        
        distance_km: parseFloat(quoteForm.distance_km) || 0,
        volume_m3: volume,
        
        camion: { type: camion, description: CAMIONS.find(c => c.value === camion)?.label || camion },
        nb_demenageurs: parseInt(nbDemenageurs),
        nb_jours: parseFloat(nbJours),
        
        prix_detail: {
          prix_transport: quoteForm.prix_transport !== '' ? parseFloat(quoteForm.prix_transport) : calculsAuto.prix_transport,
          prix_volume: quoteForm.prix_volume !== '' ? parseFloat(quoteForm.prix_volume) : calculsAuto.prix_volume,
          prix_main_oeuvre: quoteForm.prix_main_oeuvre !== '' ? parseFloat(quoteForm.prix_main_oeuvre) : calculsAuto.prix_main_oeuvre,
          prix_cartons: quoteForm.prix_cartons !== '' ? parseFloat(quoteForm.prix_cartons) : calculsAuto.prix_cartons,
          prix_stockage: quoteForm.prix_stockage !== '' ? parseFloat(quoteForm.prix_stockage) : calculsAuto.prix_stockage,
          prix_options: quoteForm.prix_options !== '' ? parseFloat(quoteForm.prix_options) : calculsAuto.prix_options,
          ajustement: parseFloat(quoteForm.ajustement) || 0,
          ajustement_description: quoteForm.ajustement_description
        },
        prix_total: prixFinal,
        
        options: {
          nb_cartons: quoteForm.nb_cartons,
          stockage: quoteForm.stockage,
          protection_complete: quoteForm.protection_complete,
          emballage_fragile: quoteForm.emballage_fragile
        },
        
        date_souhaitee: quoteForm.date_souhaitee,
        notes: quoteForm.notes,
        demande_id: selectedDemande?.demande_id
      };
      
      const response = await axios.post(`${API}/devis/sauvegarder`, devisData, { withCredentials: true });
      
      if (sendEmail && quoteForm.client_email) {
        await axios.post(`${API}/devis/${response.data.devis_id}/envoyer-email`, {}, { withCredentials: true });
        alert('Devis sauvegardé et envoyé par email !');
      } else {
        alert('Devis sauvegardé avec succès !');
      }
      
      if (selectedDemande) {
        await updateDemandeStatus(selectedDemande.demande_id, sendEmail ? 'devis_envoye' : 'en_cours');
      }
      
      setShowQuoteModal(false);
      fetchData();
      
    } catch (error) {
      console.error('Failed to save quote:', error);
      alert('Erreur lors de la sauvegarde du devis');
    } finally {
      setSavingQuote(false);
    }
  };

  // Générer PDF du devis
  const generatePDF = () => {
    const volume = volumeEffectif;
    const camion = quoteForm.camion || calculsAuto.camion_suggere;
    const nbDemenageurs = quoteForm.nb_demenageurs || calculsAuto.demenageurs_suggeres;
    const nbJours = quoteForm.nb_jours || calculsAuto.jours_suggeres;
    
    const content = `
═══════════════════════════════════════════════════════
                    DEVIS DE DÉMÉNAGEMENT
═══════════════════════════════════════════════════════

DLV EXPRESS
14 avenue des Grands Champs
95500 Le Thillay
Tél: 06 41 88 89 52
Email: transport@dlv-express.com

Date: ${new Date().toLocaleDateString('fr-FR')}
Validité: 30 jours

───────────────────────────────────────────────────────
                    INFORMATIONS CLIENT
───────────────────────────────────────────────────────

Nom: ${quoteForm.client_nom}
Téléphone: ${quoteForm.client_telephone}
Email: ${quoteForm.client_email}

───────────────────────────────────────────────────────
                    ADRESSES
───────────────────────────────────────────────────────

DÉPART:
${quoteForm.depart_adresse}
${quoteForm.depart_code_postal} ${quoteForm.depart_ville}
Étage: ${quoteForm.depart_etage} | Ascenseur: ${quoteForm.depart_ascenseur ? 'Oui' : 'Non'}

ARRIVÉE:
${quoteForm.arrivee_adresse}
${quoteForm.arrivee_code_postal} ${quoteForm.arrivee_ville}
Étage: ${quoteForm.arrivee_etage} | Ascenseur: ${quoteForm.arrivee_ascenseur ? 'Oui' : 'Non'}

───────────────────────────────────────────────────────
                    DÉTAILS DU DÉMÉNAGEMENT
───────────────────────────────────────────────────────

Distance: ${quoteForm.distance_km || '0'} km
Volume: ${volume} m³
Camion: ${CAMIONS.find(c => c.value === camion)?.label || camion}
Équipe: ${nbDemenageurs} déménageurs
Durée estimée: ${nbJours} jour(s)
Date souhaitée: ${quoteForm.date_souhaitee || 'À définir'}

───────────────────────────────────────────────────────
                    DÉTAIL DES PRIX
───────────────────────────────────────────────────────

Transport (${quoteForm.distance_km || 0} km × 3,25€)........ ${(quoteForm.prix_transport !== '' ? quoteForm.prix_transport : calculsAuto.prix_transport).toFixed(2)} €
Volume (${volume} m³ × 30€)................... ${(quoteForm.prix_volume !== '' ? quoteForm.prix_volume : calculsAuto.prix_volume).toFixed(2)} €
Main d'œuvre (${nbDemenageurs} × ${nbJours}j × 120€)... ${(quoteForm.prix_main_oeuvre !== '' ? quoteForm.prix_main_oeuvre : calculsAuto.prix_main_oeuvre).toFixed(2)} €
${quoteForm.nb_cartons > 0 ? `Cartons (${quoteForm.nb_cartons} × 3,80€).............. ${calculsAuto.prix_cartons.toFixed(2)} €` : ''}
${quoteForm.stockage ? `Stockage ${quoteForm.stockage}........................ ${calculsAuto.prix_stockage.toFixed(2)} €` : ''}
${quoteForm.protection_complete || quoteForm.emballage_fragile ? `Options............................ ${calculsAuto.prix_options.toFixed(2)} €` : ''}
${quoteForm.ajustement ? `Ajustement: ${quoteForm.ajustement_description || ''}... ${quoteForm.ajustement} €` : ''}

═══════════════════════════════════════════════════════
           TOTAL TTC: ${prixFinal} €
═══════════════════════════════════════════════════════

${quoteForm.notes ? `\nNotes: ${quoteForm.notes}` : ''}

───────────────────────────────────────────────────────
                    CONDITIONS
───────────────────────────────────────────────────────

• Ce devis est valable 30 jours
• Prix incluant: transport, chargement, déchargement
• Nous ne proposons PAS de monte-meuble
• Nous ne faisons PAS de démontage/remontage de meubles
• Déménagement par ascenseur ou escaliers uniquement

Pour confirmer votre déménagement, contactez-nous au 06 41 88 89 52
    `;
    
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `devis_demenagement_${quoteForm.client_nom.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const savePlanning = async () => {
    if (!selectedDemande || !planningData.date) return;
    
    try {
      // Update status to planifie
      await updateDemandeStatus(selectedDemande.demande_id, 'planifie');
      setShowPlanningModal(false);
      alert('Mission planifiée avec succès !');
    } catch (error) {
      console.error('Failed to save planning:', error);
      alert('Erreur lors de la planification');
    }
  };

  // Ouvrir le modal de création de devis
  const openQuoteModal = (demande = null) => {
    if (demande) {
      setSelectedDemande(demande);
      prefillFromDemande(demande);
    } else {
      setSelectedDemande(null);
      setQuoteForm({
        client_nom: '', client_telephone: '', client_email: '',
        depart_adresse: '', depart_code_postal: '', depart_ville: '',
        depart_etage: 0, depart_ascenseur: false,
        arrivee_adresse: '', arrivee_code_postal: '', arrivee_ville: '',
        arrivee_etage: 0, arrivee_ascenseur: false,
        distance_km: '', distance_auto: false,
        mode_volume: 'volume', volume_m3: '', surface_m2: '',
        camion: '', nb_demenageurs: '', nb_jours: '',
        nb_cartons: 0, stockage: '', protection_complete: false, emballage_fragile: false,
        prix_transport: '', prix_volume: '', prix_main_oeuvre: '',
        prix_cartons: '', prix_stockage: '', prix_options: '',
        ajustement: '', ajustement_description: '',
        notes: '', date_souhaitee: '',
      });
    }
    setShowQuoteModal(true);
  };

  // Filter demandes
  const filteredDemandes = useMemo(() => {
    let filtered = [...demandes];
    
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(d => 
        d.name?.toLowerCase().includes(search) ||
        d.phone?.includes(search) ||
        d.email?.toLowerCase().includes(search) ||
        d.address_from?.toLowerCase().includes(search) ||
        d.address_to?.toLowerCase().includes(search)
      );
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(d => d.status === statusFilter);
    }
    
    return filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [demandes, searchTerm, statusFilter]);

  // Stats
  const stats = useMemo(() => {
    return {
      total: demandes.length,
      nouveau: demandes.filter(d => d.status === 'nouveau').length,
      en_cours: demandes.filter(d => d.status === 'en_cours' || d.status === 'devis_envoye').length,
      accepte: demandes.filter(d => d.status === 'accepte' || d.status === 'planifie').length,
      devis_total: devisList.length,
    };
  }, [demandes, devisList]);

  if (loading) {
    return (
      <Layout user={user} activeModule={activeModule}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} activeModule={activeModule}>
      <div className="space-y-6" data-testid="admin-demenagement">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Home className="w-7 h-7 text-orange-600" />
              Déménagement
            </h1>
            <p className="text-slate-500 mt-1">Gestion des demandes et devis de déménagement</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => openQuoteModal()} className="bg-orange-500 hover:bg-orange-600">
              <Plus className="w-4 h-4 mr-2" />
              Nouveau devis
            </Button>
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualiser
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 font-medium">Nouvelles</p>
                  <p className="text-2xl font-bold text-blue-900">{stats.nouveau}</p>
                </div>
                <AlertCircle className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-yellow-50 to-white border-yellow-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-yellow-600 font-medium">En cours</p>
                  <p className="text-2xl font-bold text-yellow-900">{stats.en_cours}</p>
                </div>
                <Clock className="w-8 h-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-50 to-white border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600 font-medium">Acceptés</p>
                  <p className="text-2xl font-bold text-green-900">{stats.accepte}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-600 font-medium">Devis créés</p>
                  <p className="text-2xl font-bold text-purple-900">{stats.devis_total}</p>
                </div>
                <FileText className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-slate-50 to-white border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 font-medium">Total demandes</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
                </div>
                <Package className="w-8 h-8 text-slate-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-slate-200">
          <button
            onClick={() => setActiveTab('demandes')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'demandes' 
                ? 'text-orange-600 border-b-2 border-orange-500' 
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <Package className="w-4 h-4 inline mr-2" />
            Demandes clients
          </button>
          <button
            onClick={() => setActiveTab('devis')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'devis' 
                ? 'text-orange-600 border-b-2 border-orange-500' 
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <FileText className="w-4 h-4 inline mr-2" />
            Devis créés
          </button>
        </div>

        {/* Demandes Tab */}
        {activeTab === 'demandes' && (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Rechercher (nom, téléphone, adresse...)"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 border rounded-lg bg-white"
                >
                  <option value="all">Tous les statuts</option>
                  {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                    <option key={key} value={key}>{config.label}</option>
                  ))}
                </select>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {filteredDemandes.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>Aucune demande de déménagement</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-y">
                      <tr>
                        <th className="text-left p-3 text-xs font-semibold text-slate-600">Date</th>
                        <th className="text-left p-3 text-xs font-semibold text-slate-600">Client</th>
                        <th className="text-left p-3 text-xs font-semibold text-slate-600">Départ → Arrivée</th>
                        <th className="text-left p-3 text-xs font-semibold text-slate-600">Volume</th>
                        <th className="text-center p-3 text-xs font-semibold text-slate-600">Statut</th>
                        <th className="text-center p-3 text-xs font-semibold text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {filteredDemandes.map(demande => {
                        const statusConfig = STATUS_CONFIG[demande.status] || STATUS_CONFIG.nouveau;
                        const StatusIcon = statusConfig.icon;
                        
                        return (
                          <tr key={demande.demande_id} className="hover:bg-slate-50">
                            <td className="p-3 text-sm">
                              {new Date(demande.created_at).toLocaleDateString('fr-FR')}
                            </td>
                            <td className="p-3">
                              <div>
                                <p className="font-medium text-sm">{demande.name}</p>
                                <p className="text-xs text-slate-500">{demande.phone}</p>
                              </div>
                            </td>
                            <td className="p-3">
                              <div className="text-sm">
                                <p className="text-green-700 truncate max-w-[200px]" title={demande.address_from}>
                                  📍 {demande.address_from}
                                </p>
                                <p className="text-red-700 truncate max-w-[200px]" title={demande.address_to}>
                                  📍 {demande.address_to}
                                </p>
                              </div>
                            </td>
                            <td className="p-3 text-sm">
                              {demande.volume || '-'}
                            </td>
                            <td className="p-3 text-center">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1 ${statusConfig.color}`}>
                                <StatusIcon className="w-3 h-3" />
                                {statusConfig.label}
                              </span>
                            </td>
                            <td className="p-3">
                              <div className="flex items-center justify-center gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    setSelectedDemande(demande);
                                    setShowDemandeModal(true);
                                  }}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-orange-600"
                                  onClick={() => openQuoteModal(demande)}
                                >
                                  <Calculator className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Devis Tab */}
        {activeTab === 'devis' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Devis créés</CardTitle>
            </CardHeader>
            <CardContent>
              {devisList.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>Aucun devis créé</p>
                  <Button onClick={() => openQuoteModal()} className="mt-4 bg-orange-500 hover:bg-orange-600">
                    <Plus className="w-4 h-4 mr-2" />
                    Créer un devis
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-y">
                      <tr>
                        <th className="text-left p-3 text-xs font-semibold text-slate-600">Date</th>
                        <th className="text-left p-3 text-xs font-semibold text-slate-600">Client</th>
                        <th className="text-left p-3 text-xs font-semibold text-slate-600">Trajet</th>
                        <th className="text-right p-3 text-xs font-semibold text-slate-600">Prix</th>
                        <th className="text-center p-3 text-xs font-semibold text-slate-600">Statut</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {devisList.map(devis => (
                        <tr key={devis.devis_id} className="hover:bg-slate-50">
                          <td className="p-3 text-sm">
                            {new Date(devis.created_at).toLocaleDateString('fr-FR')}
                          </td>
                          <td className="p-3">
                            <p className="font-medium text-sm">{devis.client?.nom}</p>
                            <p className="text-xs text-slate-500">{devis.client?.telephone}</p>
                          </td>
                          <td className="p-3 text-sm">
                            <p className="truncate max-w-[200px]">{devis.adresse_depart}</p>
                            <p className="truncate max-w-[200px] text-slate-500">→ {devis.adresse_arrivee}</p>
                          </td>
                          <td className="p-3 text-right">
                            <span className="font-bold text-orange-600">{devis.prix_total} €</span>
                          </td>
                          <td className="p-3 text-center">
                            <Badge variant={devis.status === 'accepte' ? 'success' : devis.status === 'envoye' ? 'warning' : 'secondary'}>
                              {devis.status || 'nouveau'}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Demande Detail Modal */}
      <Dialog open={showDemandeModal} onOpenChange={setShowDemandeModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Home className="w-5 h-5 text-orange-500" />
              Détail de la demande
            </DialogTitle>
          </DialogHeader>
          
          {selectedDemande && (
            <div className="space-y-6">
              {/* Status */}
              <div>
                <Label className="text-slate-700 mb-2 block">Statut</Label>
                <select
                  value={selectedDemande.status}
                  onChange={(e) => updateDemandeStatus(selectedDemande.demande_id, e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg"
                >
                  {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                    <option key={key} value={key}>{config.label}</option>
                  ))}
                </select>
              </div>

              {/* Client Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-500 text-sm">Nom</Label>
                  <p className="font-medium">{selectedDemande.name}</p>
                </div>
                <div>
                  <Label className="text-slate-500 text-sm">Téléphone</Label>
                  <a 
                    href={`https://wa.me/33${selectedDemande.phone?.replace(/^0/, '').replace(/\s/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-green-600 hover:underline flex items-center gap-1"
                  >
                    <MessageCircle className="w-4 h-4" />
                    {selectedDemande.phone}
                  </a>
                </div>
                <div className="col-span-2">
                  <Label className="text-slate-500 text-sm">Email</Label>
                  <a href={`mailto:${selectedDemande.email}`} className="font-medium text-blue-600 hover:underline">
                    {selectedDemande.email}
                  </a>
                </div>
              </div>

              {/* Addresses */}
              <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label className="text-slate-500 text-sm flex items-center gap-1">
                    <MapPin className="w-3 h-3 text-green-500" /> Adresse de départ
                  </Label>
                  <p className="font-medium">{selectedDemande.address_from}</p>
                </div>
                <div>
                  <Label className="text-slate-500 text-sm flex items-center gap-1">
                    <MapPin className="w-3 h-3 text-red-500" /> Adresse d'arrivée
                  </Label>
                  <p className="font-medium">{selectedDemande.address_to}</p>
                </div>
              </div>

              {/* Details */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-500 text-sm">Volume estimé</Label>
                  <p className="font-medium">{selectedDemande.volume || 'Non précisé'}</p>
                </div>
                <div>
                  <Label className="text-slate-500 text-sm">Date souhaitée</Label>
                  <p className="font-medium">{selectedDemande.date || 'Flexible'}</p>
                </div>
              </div>

              {selectedDemande.message && (
                <div>
                  <Label className="text-slate-500 text-sm">Message du client</Label>
                  <p className="p-3 bg-slate-100 rounded-lg text-sm">{selectedDemande.message}</p>
                </div>
              )}

              <div className="text-xs text-slate-400">
                Demande reçue le {new Date(selectedDemande.created_at).toLocaleDateString('fr-FR')} à {new Date(selectedDemande.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2 pt-4 border-t">
                <Button
                  onClick={() => {
                    setShowDemandeModal(false);
                    openQuoteModal(selectedDemande);
                  }}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  <Calculator className="w-4 h-4 mr-2" />
                  Créer un devis
                </Button>
                <a
                  href={`https://wa.me/33${selectedDemande.phone?.replace(/^0/, '').replace(/\s/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="outline" className="text-green-600 border-green-600 hover:bg-green-50">
                    <MessageCircle className="w-4 h-4 mr-2" />
                    WhatsApp
                  </Button>
                </a>
                <Button
                  onClick={() => deleteDemande(selectedDemande.demande_id)}
                  variant="outline"
                  className="text-red-600 border-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Supprimer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Quote Modal - FORMULAIRE COMPLET */}
      <Dialog open={showQuoteModal} onOpenChange={setShowQuoteModal}>
        <DialogContent className="max-w-4xl max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calculator className="w-5 h-5 text-orange-500" />
              Créer un devis de déménagement
            </DialogTitle>
          </DialogHeader>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Colonne gauche - Informations */}
            <div className="space-y-6">
              {/* Client */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h3 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                  <User className="w-4 h-4" /> Informations client
                </h3>
                <div className="space-y-3">
                  <div>
                    <Label className="text-sm">Nom *</Label>
                    <Input
                      value={quoteForm.client_nom}
                      onChange={(e) => setQuoteForm({...quoteForm, client_nom: e.target.value})}
                      placeholder="Jean Dupont"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-sm">Téléphone *</Label>
                      <Input
                        value={quoteForm.client_telephone}
                        onChange={(e) => setQuoteForm({...quoteForm, client_telephone: e.target.value})}
                        placeholder="06 XX XX XX XX"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Email</Label>
                      <Input
                        value={quoteForm.client_email}
                        onChange={(e) => setQuoteForm({...quoteForm, client_email: e.target.value})}
                        placeholder="email@exemple.com"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Adresse départ */}
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <h3 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> Adresse de départ
                </h3>
                <div className="space-y-3">
                  <div>
                    <Label className="text-sm">Adresse *</Label>
                    <Input
                      value={quoteForm.depart_adresse}
                      onChange={(e) => setQuoteForm({...quoteForm, depart_adresse: e.target.value})}
                      placeholder="14 rue de la Paix"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-sm">Code postal</Label>
                      <Input
                        value={quoteForm.depart_code_postal}
                        onChange={(e) => setQuoteForm({...quoteForm, depart_code_postal: e.target.value})}
                        placeholder="75002"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Ville</Label>
                      <Input
                        value={quoteForm.depart_ville}
                        onChange={(e) => setQuoteForm({...quoteForm, depart_ville: e.target.value})}
                        placeholder="Paris"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-sm">Étage</Label>
                      <select
                        value={quoteForm.depart_etage}
                        onChange={(e) => setQuoteForm({...quoteForm, depart_etage: parseInt(e.target.value)})}
                        className="w-full px-3 py-2 border rounded-lg bg-white text-sm"
                      >
                        <option value={0}>RDC</option>
                        {[1,2,3,4,5,6,7,8,9,10].map(n => <option key={n} value={n}>{n}e étage</option>)}
                      </select>
                    </div>
                    <div>
                      <Label className="text-sm">Ascenseur</Label>
                      <select
                        value={quoteForm.depart_ascenseur ? 'oui' : 'non'}
                        onChange={(e) => setQuoteForm({...quoteForm, depart_ascenseur: e.target.value === 'oui'})}
                        className="w-full px-3 py-2 border rounded-lg bg-white text-sm"
                      >
                        <option value="non">Non</option>
                        <option value="oui">Oui</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Adresse arrivée */}
              <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                <h3 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> Adresse d'arrivée
                </h3>
                <div className="space-y-3">
                  <div>
                    <Label className="text-sm">Adresse *</Label>
                    <Input
                      value={quoteForm.arrivee_adresse}
                      onChange={(e) => setQuoteForm({...quoteForm, arrivee_adresse: e.target.value})}
                      placeholder="25 avenue des Champs"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-sm">Code postal</Label>
                      <Input
                        value={quoteForm.arrivee_code_postal}
                        onChange={(e) => setQuoteForm({...quoteForm, arrivee_code_postal: e.target.value})}
                        placeholder="92100"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Ville</Label>
                      <Input
                        value={quoteForm.arrivee_ville}
                        onChange={(e) => setQuoteForm({...quoteForm, arrivee_ville: e.target.value})}
                        placeholder="Boulogne"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-sm">Étage</Label>
                      <select
                        value={quoteForm.arrivee_etage}
                        onChange={(e) => setQuoteForm({...quoteForm, arrivee_etage: parseInt(e.target.value)})}
                        className="w-full px-3 py-2 border rounded-lg bg-white text-sm"
                      >
                        <option value={0}>RDC</option>
                        {[1,2,3,4,5,6,7,8,9,10].map(n => <option key={n} value={n}>{n}e étage</option>)}
                      </select>
                    </div>
                    <div>
                      <Label className="text-sm">Ascenseur</Label>
                      <select
                        value={quoteForm.arrivee_ascenseur ? 'oui' : 'non'}
                        onChange={(e) => setQuoteForm({...quoteForm, arrivee_ascenseur: e.target.value === 'oui'})}
                        className="w-full px-3 py-2 border rounded-lg bg-white text-sm"
                      >
                        <option value="non">Non</option>
                        <option value="oui">Oui</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Distance et Volume */}
              <div className="p-4 bg-slate-50 rounded-lg border">
                <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <Truck className="w-4 h-4" /> Distance & Volume
                </h3>
                <div className="space-y-3">
                  <div>
                    <Label className="text-sm">Distance (km)</Label>
                    <Input
                      type="number"
                      value={quoteForm.distance_km}
                      onChange={(e) => setQuoteForm({...quoteForm, distance_km: e.target.value})}
                      placeholder="Saisir la distance"
                    />
                    <p className="text-xs text-slate-500 mt-1">Calculez via Google Maps ou saisissez manuellement</p>
                  </div>
                  
                  <div>
                    <Label className="text-sm">Mode de calcul du volume</Label>
                    <div className="flex gap-2 mt-1">
                      <button
                        onClick={() => setQuoteForm({...quoteForm, mode_volume: 'volume'})}
                        className={`flex-1 px-3 py-2 text-sm rounded-lg border ${quoteForm.mode_volume === 'volume' ? 'bg-orange-100 border-orange-500 text-orange-700' : 'bg-white'}`}
                      >
                        Volume (m³)
                      </button>
                      <button
                        onClick={() => setQuoteForm({...quoteForm, mode_volume: 'surface'})}
                        className={`flex-1 px-3 py-2 text-sm rounded-lg border ${quoteForm.mode_volume === 'surface' ? 'bg-orange-100 border-orange-500 text-orange-700' : 'bg-white'}`}
                      >
                        Surface (m²)
                      </button>
                    </div>
                  </div>
                  
                  {quoteForm.mode_volume === 'volume' ? (
                    <div>
                      <Label className="text-sm">Volume (m³)</Label>
                      <Input
                        type="number"
                        value={quoteForm.volume_m3}
                        onChange={(e) => setQuoteForm({...quoteForm, volume_m3: e.target.value})}
                        placeholder="30"
                      />
                    </div>
                  ) : (
                    <div>
                      <Label className="text-sm">Surface du logement (m²)</Label>
                      <Input
                        type="number"
                        value={quoteForm.surface_m2}
                        onChange={(e) => setQuoteForm({...quoteForm, surface_m2: e.target.value})}
                        placeholder="60"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Volume estimé: <strong>{volumeFromSurface} m³</strong> (1m² ≈ 0.5m³)
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Colonne droite - Ressources & Prix */}
            <div className="space-y-6">
              {/* Ressources */}
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <h3 className="font-semibold text-purple-800 mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4" /> Ressources
                </h3>
                <div className="space-y-3">
                  <div>
                    <Label className="text-sm">
                      Camion 
                      <span className="text-purple-600 ml-1">(suggéré: {CAMIONS.find(c => c.value === calculsAuto.camion_suggere)?.label})</span>
                    </Label>
                    <select
                      value={quoteForm.camion || calculsAuto.camion_suggere}
                      onChange={(e) => setQuoteForm({...quoteForm, camion: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg bg-white"
                    >
                      {CAMIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-sm">
                        Déménageurs
                        <span className="text-purple-600 ml-1">(suggéré: {calculsAuto.demenageurs_suggeres})</span>
                      </Label>
                      <Input
                        type="number"
                        min="1"
                        max="10"
                        value={quoteForm.nb_demenageurs || calculsAuto.demenageurs_suggeres}
                        onChange={(e) => setQuoteForm({...quoteForm, nb_demenageurs: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label className="text-sm">
                        Durée (jours)
                        <span className="text-purple-600 ml-1">(suggéré: {calculsAuto.jours_suggeres})</span>
                      </Label>
                      <Input
                        type="number"
                        step="0.5"
                        min="0.5"
                        value={quoteForm.nb_jours || calculsAuto.jours_suggeres}
                        onChange={(e) => setQuoteForm({...quoteForm, nb_jours: e.target.value})}
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm">Date souhaitée</Label>
                    <Input
                      type="date"
                      value={quoteForm.date_souhaitee}
                      onChange={(e) => setQuoteForm({...quoteForm, date_souhaitee: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              {/* Options */}
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <h3 className="font-semibold text-amber-800 mb-3 flex items-center gap-2">
                  <Box className="w-4 h-4" /> Options
                </h3>
                <div className="space-y-3">
                  <div>
                    <Label className="text-sm">Cartons ({DEVIS_CONFIG.prix_carton}€/u)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={quoteForm.nb_cartons}
                      onChange={(e) => setQuoteForm({...quoteForm, nb_cartons: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Stockage temporaire</Label>
                    <select
                      value={quoteForm.stockage}
                      onChange={(e) => setQuoteForm({...quoteForm, stockage: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg bg-white"
                    >
                      <option value="">Aucun</option>
                      <option value="24h">24h - {DEVIS_CONFIG.prix_stockage_24h}€</option>
                      <option value="48h">48h - {DEVIS_CONFIG.prix_stockage_48h}€</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={quoteForm.protection_complete}
                        onChange={(e) => setQuoteForm({...quoteForm, protection_complete: e.target.checked})}
                        className="w-4 h-4 accent-orange-500"
                      />
                      <span className="text-sm">Protection complète ({DEVIS_CONFIG.prix_protection}€)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={quoteForm.emballage_fragile}
                        onChange={(e) => setQuoteForm({...quoteForm, emballage_fragile: e.target.checked})}
                        className="w-4 h-4 accent-orange-500"
                      />
                      <span className="text-sm">Emballage fragile ({DEVIS_CONFIG.prix_emballage_fragile}€)</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Prix détaillé */}
              <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                <h3 className="font-semibold text-orange-800 mb-3 flex items-center gap-2">
                  <Euro className="w-4 h-4" /> Détail des prix (modifiables)
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span>Transport ({quoteForm.distance_km || 0} km × 3,25€)</span>
                    <Input
                      type="number"
                      className="w-24 h-8 text-right"
                      value={quoteForm.prix_transport !== '' ? quoteForm.prix_transport : calculsAuto.prix_transport}
                      onChange={(e) => setQuoteForm({...quoteForm, prix_transport: e.target.value})}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Volume ({volumeEffectif} m³ × 30€)</span>
                    <Input
                      type="number"
                      className="w-24 h-8 text-right"
                      value={quoteForm.prix_volume !== '' ? quoteForm.prix_volume : calculsAuto.prix_volume}
                      onChange={(e) => setQuoteForm({...quoteForm, prix_volume: e.target.value})}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Main d'œuvre</span>
                    <Input
                      type="number"
                      className="w-24 h-8 text-right"
                      value={quoteForm.prix_main_oeuvre !== '' ? quoteForm.prix_main_oeuvre : calculsAuto.prix_main_oeuvre}
                      onChange={(e) => setQuoteForm({...quoteForm, prix_main_oeuvre: e.target.value})}
                    />
                  </div>
                  {(quoteForm.nb_cartons > 0 || calculsAuto.prix_cartons > 0) && (
                    <div className="flex items-center justify-between">
                      <span>Cartons</span>
                      <Input
                        type="number"
                        className="w-24 h-8 text-right"
                        value={quoteForm.prix_cartons !== '' ? quoteForm.prix_cartons : calculsAuto.prix_cartons}
                        onChange={(e) => setQuoteForm({...quoteForm, prix_cartons: e.target.value})}
                      />
                    </div>
                  )}
                  {(quoteForm.stockage || calculsAuto.prix_stockage > 0) && (
                    <div className="flex items-center justify-between">
                      <span>Stockage</span>
                      <span className="font-medium">{calculsAuto.prix_stockage} €</span>
                    </div>
                  )}
                  {calculsAuto.prix_options > 0 && (
                    <div className="flex items-center justify-between">
                      <span>Options</span>
                      <span className="font-medium">{calculsAuto.prix_options} €</span>
                    </div>
                  )}
                  
                  <div className="border-t border-orange-300 pt-2 mt-2">
                    <div className="flex items-center justify-between">
                      <span>Ajustement manuel (±)</span>
                      <Input
                        type="number"
                        className="w-24 h-8 text-right"
                        value={quoteForm.ajustement}
                        onChange={(e) => setQuoteForm({...quoteForm, ajustement: e.target.value})}
                        placeholder="0"
                      />
                    </div>
                    {quoteForm.ajustement && (
                      <Input
                        className="mt-1 h-8 text-sm"
                        placeholder="Raison de l'ajustement..."
                        value={quoteForm.ajustement_description}
                        onChange={(e) => setQuoteForm({...quoteForm, ajustement_description: e.target.value})}
                      />
                    )}
                  </div>
                </div>
                
                <div className="mt-4 p-3 bg-orange-100 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-orange-800">TOTAL TTC</span>
                    <span className="text-3xl font-bold text-orange-600">{prixFinal} €</span>
                  </div>
                  <p className="text-xs text-orange-600 mt-1">Minimum: {DEVIS_CONFIG.minimum_devis}€ • Arrondi à 10€</p>
                </div>
              </div>

              {/* Notes */}
              <div>
                <Label className="text-sm">Notes internes</Label>
                <Textarea
                  value={quoteForm.notes}
                  onChange={(e) => setQuoteForm({...quoteForm, notes: e.target.value})}
                  placeholder="Notes supplémentaires..."
                  rows={2}
                />
              </div>
            </div>
          </div>

          {/* Footer avec actions */}
          <DialogFooter className="flex flex-col sm:flex-row gap-2 mt-6 pt-4 border-t">
            <div className="flex-1 flex gap-2">
              <Button variant="outline" onClick={() => setShowQuoteModal(false)}>
                Annuler
              </Button>
              <Button variant="outline" onClick={generatePDF}>
                <Download className="w-4 h-4 mr-2" />
                Télécharger
              </Button>
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={() => saveQuote(false)}
                disabled={savingQuote || !quoteForm.client_nom}
                variant="outline"
                className="border-orange-500 text-orange-600"
              >
                Sauvegarder
              </Button>
              <Button 
                onClick={() => saveQuote(true)}
                disabled={savingQuote || !quoteForm.client_nom || !quoteForm.client_email}
                className="bg-orange-500 hover:bg-orange-600"
              >
                <Send className="w-4 h-4 mr-2" />
                {savingQuote ? 'Envoi...' : 'Envoyer par email'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Planning Modal */}
      <Dialog open={showPlanningModal} onOpenChange={setShowPlanningModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-orange-500" />
              Planifier la mission
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Date</Label>
              <Input
                type="date"
                value={planningData.date}
                onChange={(e) => setPlanningData({...planningData, date: e.target.value})}
              />
            </div>
            <div>
              <Label>Heure de départ</Label>
              <Input
                type="time"
                value={planningData.time}
                onChange={(e) => setPlanningData({...planningData, time: e.target.value})}
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={planningData.notes}
                onChange={(e) => setPlanningData({...planningData, notes: e.target.value})}
                placeholder="Instructions particulières..."
                rows={3}
              />
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowPlanningModal(false)} className="flex-1">
                Annuler
              </Button>
              <Button onClick={savePlanning} className="flex-1 bg-orange-500 hover:bg-orange-600">
                Confirmer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default AdminDemenagement;
